import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { QUERY_KEYS } from '../../../../../api/queryKeys'
import WealthboxEventRow from '../shared/WealthboxEventRow'
import WealthboxEventDetailModal from '../shared/WealthboxEventDetailModal'
import CreateWealthboxEventModal from '../shared/CreateWealthboxEventModal'

const EVENT_INVALIDATE_KEYS = [
  [QUERY_KEYS.wealthboxDomainQuery, 'list-preset']
]

function WealthboxEventsContent ({ items, limit, config, contactId, createOpen, onCloseCreate }) {
  const [selectedEvent, setSelectedEvent] = useState(null)

  const enableEdit = config?.enableEdit ?? false
  const formMode = config?.formMode ?? 'calendar'
  const scope = config?.scope || 'client'

  // Events return server-side sorted asc when we passed `order=asc` in the
  // preset's params, but defend with a local sort anyway so a server that
  // doesn't honor `order` still gives chronological output.
  const sorted = useMemo(() => {
    const arr = Array.isArray(items) ? [...items] : []
    arr.sort((a, b) => {
      const da = a.starts_at ? new Date(a.starts_at).getTime() : Number.POSITIVE_INFINITY
      const db = b.starts_at ? new Date(b.starts_at).getTime() : Number.POSITIVE_INFINITY
      return da - db
    })
    return arr.slice(0, limit)
  }, [items, limit])

  const handleRowClick = useCallback((event) => setSelectedEvent(event), [])
  const handleCloseDetail = useCallback(() => setSelectedEvent(null), [])

  // IMPORTANT: do NOT early-return on empty state. The header-level Create
  // button lives in the parent renderer and controls `createOpen` via props;
  // the actual <CreateWealthboxEventModal> instance lives down here. If we
  // bailed when `sorted.length === 0` the create modal would never mount and
  // clicking Create would silently do nothing.
  return (
    <>
      {sorted.length
        ? (
          <div>
            {sorted.map(event => (
              <WealthboxEventRow
                key={event.id}
                event={event}
                onClick={() => handleRowClick(event)}
              />
            ))}
          </div>
        )
        : (
          <div className='p-md font-size-sm' style={{ color: 'var(--text-color-subtle)' }}>
            No upcoming events match the current filters.
          </div>
        )}

      <WealthboxEventDetailModal
        open={!!selectedEvent}
        onClose={handleCloseDetail}
        event={selectedEvent}
        enableEdit={enableEdit}
        formMode={formMode}
        invalidateQueryKeys={EVENT_INVALIDATE_KEYS}
      />

      <CreateWealthboxEventModal
        open={createOpen}
        onClose={onCloseCreate}
        scope={scope}
        contactId={contactId}
        formMode={formMode}
        invalidateQueryKeys={EVENT_INVALIDATE_KEYS}
      />
    </>
  )
}

WealthboxEventsContent.propTypes = {
  items: PropTypes.array,
  limit: PropTypes.number,
  config: PropTypes.object,
  contactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  createOpen: PropTypes.bool,
  onCloseCreate: PropTypes.func
}

export default WealthboxEventsContent
